import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Fleet", to: "/fleet" },
  { label: "Airport Transportation", to: "/airport-transportation" },
  { label: "Rates", to: "/rates" },
  { label: "Services", to: "/#services" },
  { label: "Contact", to: "/#contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleNavClick = (e, to) => {
    if (to === "/") {
      e.preventDefault();
      setOpen(false);
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => window.scrollTo(0, 0), 50);
      }
      return;
    }
    if (!to.startsWith("/#")) return;
    e.preventDefault();
    setOpen(false);
    const id = to.slice(2);
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-gradient-to-b from-black/90 via-black/65 to-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Link to="/" data-testid="navbar-logo" onClick={(e) => handleNavClick(e, "/")} className="flex items-center gap-2">
            <Logo imgClass="h-10 w-10" textClass="text-2xl" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              onClick={(e) => handleNavClick(e, l.to)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="nav-cta text-sm tracking-wide"
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="nav-dashboard-link"
                className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
              >
                <LayoutDashboard size={16} strokeWidth={1.5} />
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                <LogOut size={16} strokeWidth={1.5} /> Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="nav-cta text-sm tracking-wide"
              >
                Sign In
              </Link>
            </>
          )}
        </nav>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass mt-3 mx-6 rounded-sm p-6 flex flex-col gap-4" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <Link key={l.label} to={l.to} onClick={(e) => handleNavClick(e, l.to)} className="text-zinc-200 text-sm" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          <div className="h-px bg-white/10 my-1" />
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-zinc-200 text-sm">
                {user.role === "admin" ? "Admin Panel" : "My Dashboard"}
              </Link>
              <button onClick={handleLogout} className="text-left text-zinc-400 text-sm">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-zinc-200 text-sm">Sign In</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
